import { type ApolloError, useApolloClient } from "@apollo/client";
import { useAccount } from "@qw/auth";
import {
  type DraftMessageAttachmentFragment,
  type DraftMessageFragment,
  File_Audience,
  File_Source,
  type FileUploadFragment,
  InboxDraftAttachmentsDocument,
  type InboxDraftAttachmentsQuery,
  type UpdateDraftMessageInputDto,
  useAddAttachmentToInboxDraftMutation,
  useInboxDraftAttachmentsQuery,
  useInboxDraftQuery,
  useInboxDraftSignatureQuery,
  useRemoveAttachmentFromInboxDraftMutation,
  useSyncInboxDraftToExternalDraftMutation,
  useUpdateInboxDraftMutation,
} from "@qw/graphql";
import {
  Button,
  DraftEmailSignatureProvider,
  downloadFile,
  EditorExtensionDraftEmailSignature,
  EditorExtensionEmojiSuggestion,
  type EmailDraftContextValue,
  EmailDraftProvider,
  uploadFiles,
  useToast,
} from "@qw/ui";
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router";
import { useDebounceCallback } from "usehooks-ts";

import { EditorExtensionContactMention } from "../components/draft/draft-editor-extensions/editor-extension-contact-mention";

import { useDeleteDraft } from "./use-delete-draft";
import { ReplyPlacement, useDraftsCompose } from "./use-drafts-compose";
import { usePage } from "./use-page";
import { useRescheduleSendDraft } from "./use-reschedule-send-draft";
import { useScheduleSendDraft } from "./use-schedule-send-draft";
import { useSendDraft } from "./use-send-draft";
import { useUnscheduleSendDraft } from "./use-unschedule-send-draft";

export const OVER_ATTACHMENT_LIMIT_HINT = "Attachments are over the 25 MB limit. Remove or shrink attachments to send.";

const DRAFT_EDITOR_EXTENSIONS = [
  EditorExtensionDraftEmailSignature,
  EditorExtensionEmojiSuggestion,
  EditorExtensionContactMention,
];

/** Server-computed attachment size state; see `DraftAttachmentSizeSummary` on the API. */
export type DraftAttachmentSizeState = NonNullable<
  NonNullable<InboxDraftAttachmentsQuery["inboxDraftAttachments"]>["sizeSummary"]
>;

interface DraftContextValue {
  draft?: DraftMessageFragment | null;
  placement: "floating" | "inline";
  isLoading: boolean;
  isSending: boolean;
  isSyncing: boolean;
  isDeleting: boolean;
  isScheduling: boolean;
  isRescheduling: boolean;
  isUnscheduling: boolean;
  isScheduled: boolean;
  isScheduledFailed: boolean;
  error: ApolloError | undefined;
  isMinimized: boolean;
  handleClose: () => void;
  handleOpenFloating: () => Promise<void>;
  handleEditInGmail: () => void;
  handleScheduleSendDraft: () => Promise<void>;
  handleRescheduleSendDraft: () => void;
  handleUnscheduleSendDraft: () => void;
  handleDeleteDraft: () => void;
  attachmentSize?: DraftAttachmentSizeState;
}

export const DraftContext = createContext<DraftContextValue>({} as DraftContextValue);

interface DraftProviderProps {
  id: string;
  placement?: "floating" | "inline";
}

export const DraftProvider: FC<PropsWithChildren<DraftProviderProps>> = ({
  id = "",
  placement = "floating",
  children,
}) => {
  const { toast } = useToast();
  const apolloClient = useApolloClient();
  const draftCardRef = useRef<HTMLDivElement>(null);
  const draftEditorContainerRef = useRef<HTMLDivElement>(null);
  const draftUpdateAbortControllerRef = useRef<AbortController>(new AbortController());
  // The update the debounce has already dispatched, so send/schedule can wait for it to land.
  const pendingDraftUpdateRef = useRef<Promise<unknown> | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const { user } = useAccount();
  const { rootPath } = usePage();
  const {
    beginPendingAttachmentUpload,
    canReturnDraftInline,
    closeDraft,
    draftsContainerRef,
    getPendingAttachmentUploadCount,
    openDraft,
    returnDraftInline,
  } = useDraftsCompose();

  const {
    data,
    loading: isLoading,
    error: draftLoadError,
  } = useInboxDraftQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });
  const { data: attachmentsData } = useInboxDraftAttachmentsQuery({
    variables: { id },
    skip: !id,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const { data: signatureData } = useInboxDraftSignatureQuery();

  const [updateDraft] = useUpdateInboxDraftMutation();
  const [deleteDraft, { loading: isDeleting }] = useDeleteDraft();
  const [sendDraft, { loading: isSending }] = useSendDraft();
  const [scheduleSendDraft, { loading: isScheduling }] = useScheduleSendDraft();
  const [rescheduleSendDraft, { loading: isRescheduling }] = useRescheduleSendDraft();
  const [unscheduleSendDraft, { loading: isUnscheduling }] = useUnscheduleSendDraft();
  const [addAttachmentToDraft] = useAddAttachmentToInboxDraftMutation();
  const [removeAttachmentFromDraft] = useRemoveAttachmentFromInboxDraftMutation();
  const [syncInboxDraftToExternalDraft, { loading: isSyncing }] = useSyncInboxDraftToExternalDraftMutation();

  const draft = data?.inboxDraft;
  const draftId = draft?.id;
  const scheduledSend = draft?.scheduledSend;
  const signature = signatureData?.inboxDraftSignature?.signature;
  const attachments = attachmentsData?.inboxDraftAttachments?.attachments;
  const attachmentSize = attachmentsData?.inboxDraftAttachments?.sizeSummary;

  const isScheduled = ["pending", "queued"].includes(draft?.scheduledSend?.status ?? "");
  const isScheduledFailed = draft?.scheduledSend?.status === "failed";
  const isEditable =
    !isLoading && !isScheduled && !isSending && !isScheduling && !isRescheduling && !isUnscheduling && !isDeleting;

  const handleClose = useCallback(() => closeDraft(id), [closeDraft, id]);
  const canReturnInline = placement === "floating" && canReturnDraftInline(id);

  const handleToggleMinimized = useCallback(() => {
    setIsMinimized(!isMinimized);
    setRecipientsOpen(false);
  }, [setIsMinimized, isMinimized]);

  const handleUpdate = useDebounceCallback(
    useCallback(
      async (value: Omit<UpdateDraftMessageInputDto, "id">) => {
        if (!draftId || !isEditable) {
          return;
        }

        draftUpdateAbortControllerRef.current.abort();
        draftUpdateAbortControllerRef.current = new AbortController();

        // `onError` already surfaces failures, so the tracked promise settles rather than rejecting
        // — waiters (send/schedule) should proceed on a failed update, not blow up.
        const updatePromise = updateDraft({
          variables: { input: { id: draftId, ...value } },
          context: { fetchOptions: { signal: draftUpdateAbortControllerRef.current.signal } },
          onError: (error) => {
            // Don't show toast if the error is caused by the abort controller, as it's expected.
            if (error.cause?.name === "AbortError") {
              return;
            }

            toast({
              title: "Error updating draft",
              description: error.message || "An unexpected error occurred",
              variant: "destructive",
            });
          },
        }).catch(() => undefined);

        pendingDraftUpdateRef.current = updatePromise;

        try {
          await updatePromise;
        } finally {
          if (pendingDraftUpdateRef.current === updatePromise) {
            pendingDraftUpdateRef.current = null;
          }
        }
      },
      [draftId, isEditable, updateDraft, toast]
    ),
    250,
    { trailing: true }
  );

  useEffect(() => {
    return () => {
      void handleUpdate.flush();
    };
  }, [handleUpdate]);

  const flushPendingDraftUpdate = useCallback(async () => {
    // `flush()` runs the debounced update synchronously as far as its first await, so by the time it
    // returns the mutation has been dispatched and registered on the ref.
    handleUpdate.flush();
    await pendingDraftUpdateRef.current;
  }, [handleUpdate]);

  const cancelPendingDraftUpdate = useCallback(() => {
    handleUpdate.cancel();
    draftUpdateAbortControllerRef.current.abort();
    draftUpdateAbortControllerRef.current = new AbortController();
    pendingDraftUpdateRef.current = null;
  }, [handleUpdate]);

  // Switching placement moves the draft between the inline and floating trees, so this provider
  // unmounts and a fresh one mounts in its place. The new instance cannot see this one's in-flight
  // update, so let the pending edit land before the swap — otherwise a send from the new editor can
  // overtake it and go out with the pre-edit body.
  const handleCloseFloating = useCallback(async () => {
    await flushPendingDraftUpdate();

    if (returnDraftInline(id)) {
      return;
    }

    closeDraft(id);
  }, [closeDraft, flushPendingDraftUpdate, id, returnDraftInline]);

  const handleOpenFloating = useCallback(async () => {
    await flushPendingDraftUpdate();
    openDraft(id, { placement: ReplyPlacement.floating });
  }, [flushPendingDraftUpdate, id, openDraft]);

  const handleUpdateRecipientsTo = useCallback((value: string[]) => handleUpdate({ to: value }), [handleUpdate]);

  const handleUpdateRecipientsCc = useCallback((value: string[]) => handleUpdate({ cc: value }), [handleUpdate]);

  const handleUpdateRecipientsBcc = useCallback((value: string[]) => handleUpdate({ bcc: value }), [handleUpdate]);

  const handleUpdateSubject = useCallback((value: string) => handleUpdate({ subject: value }), [handleUpdate]);

  const handleUpdateContent = useCallback(
    (value: { html: string; text: string }) => handleUpdate({ html: value.html, text: value.text }),
    [handleUpdate]
  );

  const handleDownloadAttachment = useCallback(
    async (attachment: DraftMessageAttachmentFragment) => {
      const { fileUpload, filename } = attachment;

      if (!fileUpload) {
        toast({
          title: "Error",
          description: "Unable to download attachment: file upload not available",
          variant: "destructive",
        });
        return;
      }

      try {
        await downloadFile(fileUpload);
        toast({
          title: "Download started",
          description: `Downloading${filename ? ` ${filename}` : ""}...`,
        });
      } catch (error) {
        toast({
          title: "Download failed",
          description: error instanceof Error ? error.message : "An error occurred while downloading the attachment",
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  const uploadAttachment = useCallback(
    async (attachment: File) => {
      if (!draftId || !isEditable) {
        return;
      }

      // In flight until addAttachmentToInboxDraft has landed or rolled back — until then the file
      // is not on the draft server-side. Tracked on the compose provider so that a placement switch
      // remounting this one mid-upload does not lose the upload along with it.
      const releasePendingUpload = beginPendingAttachmentUpload(draftId);

      try {
        const inboxDraftAttachmentsQuery = apolloClient.readQuery<InboxDraftAttachmentsQuery>({
          query: InboxDraftAttachmentsDocument,
          variables: { id: draftId },
        });

        const existingDraftAttachments = inboxDraftAttachmentsQuery?.inboxDraftAttachments;

        // Generate a hash that will be unique and we can use to identify the attachment in the cache
        // and use it to update the cache with the real attachment data from the server.
        const tempId = `temp_${crypto.randomUUID()}`;

        const tempAttachment: DraftMessageAttachmentFragment = {
          __typename: "DraftMessageAttachment",
          id: tempId,
          filename: attachment.name,
          contentType: attachment.type,
          size: attachment.size,
          fileUploadId: "",
          fileUpload: {} as FileUploadFragment,
        };

        if (existingDraftAttachments) {
          apolloClient.writeQuery({
            query: InboxDraftAttachmentsDocument,
            variables: { id: draftId },
            data: {
              inboxDraftAttachments: {
                ...existingDraftAttachments,
                attachments: [...existingDraftAttachments.attachments, tempAttachment],
              },
            },
          });
        }

        // Roll back only THIS upload's optimistic row, reading the CURRENT cache so a
        // concurrent sibling upload's temp/real row is preserved (handleUploadAttachments
        // uploads attachments in parallel).
        const rollbackTempAttachment = () => {
          const current = apolloClient.readQuery<InboxDraftAttachmentsQuery>({
            query: InboxDraftAttachmentsDocument,
            variables: { id: draftId },
          })?.inboxDraftAttachments;

          if (current) {
            apolloClient.writeQuery({
              query: InboxDraftAttachmentsDocument,
              variables: { id: draftId },
              data: {
                inboxDraftAttachments: {
                  ...current,
                  attachments: current.attachments.filter((a) => a.id !== tempId),
                },
              },
            });
          }
        };

        let uploadedFileId: string;
        try {
          // Upload the attachment to the server
          const uploadedFiles = await uploadFiles([attachment], {
            audience: File_Audience.Internal,
            source: File_Source.ManualUpload,
          });
          const fileId = uploadedFiles.fileId[0];

          if (!fileId) {
            throw new Error("Upload succeeded but no file ID was returned");
          }

          uploadedFileId = fileId;
        } catch (error) {
          toast({
            title: `Error uploading attachment ${attachment.name}`,
            description: error instanceof Error ? error.message : "An error occurred while uploading the attachment",
            variant: "destructive",
          });

          rollbackTempAttachment();
          return;
        }

        // Add the attachment to the draft
        const result = await addAttachmentToDraft({
          variables: { input: { id: draftId, fileUploadId: uploadedFileId } },
          // The optimistic cache write below covers the attachment row, but `sizeSummary` is
          // computed server-side — refetch so the running total and Send guard stay accurate.
          refetchQueries: ["InboxDraftAttachments"],
          onError: (error) => {
            toast({
              title: `Error adding attachment ${attachment.name} to draft`,
              description: error instanceof Error ? error.message : "An error occurred while adding the attachment",
              variant: "destructive",
            });

            // Revert only this upload's optimistic row, preserving concurrent siblings
            rollbackTempAttachment();
          },
        });

        // Read the draft attachments from the cache
        const updatedInboxDraftAttachmentsQuery = apolloClient.readQuery<InboxDraftAttachmentsQuery>({
          query: InboxDraftAttachmentsDocument,
          variables: { id: draftId },
        });

        // Get the updated draft attachments from the cache
        const updatedInboxDraftAttachments = updatedInboxDraftAttachmentsQuery?.inboxDraftAttachments;

        // Find the temp attachment index in the cache
        const tempAttachmentIndex = updatedInboxDraftAttachments?.attachments?.findIndex((a) => a.id === tempId);

        // Update the attachment at the index with the real attachment data from the server
        if (
          result.data?.addAttachmentToInboxDraft &&
          updatedInboxDraftAttachments &&
          tempAttachmentIndex !== undefined &&
          tempAttachmentIndex >= 0
        ) {
          const updatedAttachments = [...updatedInboxDraftAttachments.attachments];
          updatedAttachments[tempAttachmentIndex] = result.data?.addAttachmentToInboxDraft;

          apolloClient.writeQuery({
            query: InboxDraftAttachmentsDocument,
            variables: { id: draftId },
            data: { inboxDraftAttachments: { ...updatedInboxDraftAttachments, attachments: updatedAttachments } },
          });
        }
      } finally {
        releasePendingUpload();
      }
    },
    [draftId, isEditable, apolloClient, addAttachmentToDraft, beginPendingAttachmentUpload, toast]
  );

  const handleUploadAttachments = useCallback(
    async (attachments: File[]) => {
      if (!isEditable) {
        return;
      }

      setTimeout(() => {
        // Scroll to the bottom of the editor container to show the new attachments
        if (!draftEditorContainerRef.current) {
          return;
        }

        const editorContainer = draftEditorContainerRef.current;
        const scrollHeight = editorContainer.scrollHeight || 0;

        editorContainer.scrollTo({ top: scrollHeight });
      }, 150);

      await Promise.all(attachments.map(uploadAttachment));
    },
    [isEditable, uploadAttachment]
  );

  const handleDeleteAttachment = useCallback(
    async (attachment: DraftMessageAttachmentFragment) => {
      if (!draftId || !isEditable) {
        return;
      }

      // Read the draft attachments from the cache
      const inboxDraftAttachmentsQuery = apolloClient.readQuery<InboxDraftAttachmentsQuery>({
        query: InboxDraftAttachmentsDocument,
        variables: { id: draftId },
      });

      // Get the existing draft attachments from the cache
      const existingDraftAttachments = inboxDraftAttachmentsQuery?.inboxDraftAttachments;

      if (existingDraftAttachments) {
        // Remove the attachment from the cache
        apolloClient.writeQuery({
          query: InboxDraftAttachmentsDocument,
          variables: { id: draftId },
          data: {
            inboxDraftAttachments: {
              ...existingDraftAttachments,
              attachments: existingDraftAttachments.attachments.filter((a) => a.id !== attachment.id),
            },
          },
        });
      }

      removeAttachmentFromDraft({
        variables: { input: { id: draftId, attachmentId: attachment.id } },
        // Keeps the server-computed `sizeSummary` in step with the optimistic removal above.
        refetchQueries: ["InboxDraftAttachments"],
        onError: (error) => {
          toast({
            title: "Attachment removal failed",
            description: error instanceof Error ? error.message : "An error occurred while removing the attachment",
            variant: "destructive",
          });

          // Revert the cache to the previous state
          if (existingDraftAttachments) {
            apolloClient.writeQuery({
              query: InboxDraftAttachmentsDocument,
              variables: { id: draftId },
              data: { inboxDraftAttachments: existingDraftAttachments },
            });
          }
        },
      });
    },
    [draftId, isEditable, apolloClient, removeAttachmentFromDraft, toast]
  );

  const handleEditInGmail = useCallback(async () => {
    if (!draftId || !user?.email) {
      return;
    }

    try {
      const { data: syncData } = await syncInboxDraftToExternalDraft({ variables: { id: draftId } });

      if (syncData?.syncInboxDraftToExternalDraft) {
        const gmailUrl = `https://mail.google.com/mail/u/#drafts?compose=${syncData.syncInboxDraftToExternalDraft.externalMessageId}`;
        window.open(gmailUrl, "_blank");
      }
    } catch (error) {
      toast({
        title: "Error opening draft in Gmail",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  }, [draftId, user, syncInboxDraftToExternalDraft]);

  const handleSendDraft = useCallback(async () => {
    if (!draftId) {
      return;
    }
    // Send goes out by draft id, so anything the debounce has not persisted yet would be missing
    // from the outgoing email. Wait for the pending edit to land rather than cancelling it, and
    // leave an in-flight update alone instead of aborting it. Only delete discards pending edits.
    await flushPendingDraftUpdate();
    sendDraft(draftId, { pendingAttachmentUploadCount: getPendingAttachmentUploadCount(draftId) });
  }, [draftId, flushPendingDraftUpdate, getPendingAttachmentUploadCount, sendDraft]);

  const handleScheduleSendDraft = useCallback(async () => {
    if (!draftId) {
      return;
    }
    await flushPendingDraftUpdate();
    scheduleSendDraft(draftId, { pendingAttachmentUploadCount: getPendingAttachmentUploadCount(draftId) });
  }, [draftId, flushPendingDraftUpdate, getPendingAttachmentUploadCount, scheduleSendDraft]);

  const handleRescheduleSendDraft = useCallback(() => {
    if (!draftId || !scheduledSend || !isScheduled) {
      return;
    }
    rescheduleSendDraft(draftId, new Date(scheduledSend.sendAt));
  }, [draftId, scheduledSend, isScheduled, rescheduleSendDraft]);

  const handleUnscheduleSendDraft = useCallback(() => {
    if (!draftId || !scheduledSend || !isScheduled) {
      return;
    }
    unscheduleSendDraft(draftId);
  }, [draftId, scheduledSend, isScheduled, unscheduleSendDraft]);

  const handleDeleteDraft = useCallback(() => {
    if (!draftId) {
      return;
    }
    cancelPendingDraftUpdate();
    deleteDraft(draftId);
  }, [cancelPendingDraftUpdate, deleteDraft, draftId]);

  // Scroll draft card into view when initially opened or when maximizing
  useEffect(() => {
    if (placement !== "floating") {
      return;
    }

    const scrollDraftIntoView = () => {
      const cardWidth = draftCardRef.current?.offsetWidth || 0;
      const cardLeft = draftCardRef.current?.offsetLeft || 0;
      const cardRight = cardLeft + cardWidth;
      const containerScrollLeft = draftsContainerRef.current?.scrollLeft || 0;
      const containerOffsetWidth = draftsContainerRef.current?.offsetWidth || 0;

      if (containerScrollLeft > cardLeft) {
        draftsContainerRef.current?.scrollTo({ left: cardLeft, behavior: "smooth" });
        return;
      }

      if (containerScrollLeft + containerOffsetWidth < cardRight) {
        draftsContainerRef.current?.scrollTo({ left: cardRight - containerOffsetWidth, behavior: "smooth" });
        return;
      }
    };

    if (!isMinimized) {
      setTimeout(scrollDraftIntoView, 50);
    }
  }, [isMinimized, draftCardRef, draftsContainerRef, placement]);

  const value = useMemo(
    () =>
      ({
        draft,
        placement,
        isLoading,
        error: draftLoadError,
        isSending,
        isSyncing,
        isMinimized,
        isDeleting,
        isScheduling,
        isRescheduling,
        isUnscheduling,
        isScheduled,
        isScheduledFailed,
        handleClose,
        handleOpenFloating,
        handleEditInGmail,
        handleScheduleSendDraft,
        handleRescheduleSendDraft,
        handleUnscheduleSendDraft,
        handleDeleteDraft,
        attachmentSize,
      }) satisfies DraftContextValue,
    [
      draft,
      placement,
      isLoading,
      draftLoadError,
      isSending,
      isSyncing,
      isMinimized,
      isDeleting,
      isScheduling,
      isRescheduling,
      isUnscheduling,
      isScheduled,
      isScheduledFailed,
      handleClose,
      handleOpenFloating,
      handleEditInGmail,
      handleScheduleSendDraft,
      handleRescheduleSendDraft,
      handleUnscheduleSendDraft,
      handleDeleteDraft,
      attachmentSize,
    ]
  );

  const emailDraftAttachments = useMemo(
    () =>
      (attachments ?? []).map((attachment) => ({
        id: attachment.id,
        name: attachment.filename ?? "",
        size: attachment.size,
        removable: true,
        pending: !attachment.fileUpload?.id,
      })),
    [attachments]
  );

  const handleDownloadAttachmentById = useCallback(
    async (attachmentId: string) => {
      const attachment = attachments?.find((a) => a.id === attachmentId);

      if (attachment) {
        await handleDownloadAttachment(attachment);
      }
    },
    [attachments, handleDownloadAttachment]
  );

  const handleDeleteAttachmentById = useCallback(
    async (attachmentId: string) => {
      const attachment = attachments?.find((a) => a.id === attachmentId);

      if (attachment) {
        await handleDeleteAttachment(attachment);
      }
    },
    [attachments, handleDeleteAttachment]
  );

  const emailDraftValue = useMemo(
    () =>
      ({
        title: draft?.subject || "Draft",
        placement,
        isMinimized,
        handleToggleMinimized,
        handleClose: handleCloseFloating,
        closeLabel: canReturnInline ? "Close floating editor" : "Close draft",
        cardRef: draftCardRef,
        editorContainerRef: draftEditorContainerRef,
        isEditable,
        isSending,
        recipientsOpen,
        setRecipientsOpen,
        recipientsReadOnly: false,
        showBcc: true,
        handleUpdateRecipientsTo,
        handleUpdateRecipientsCc,
        handleUpdateRecipientsBcc,
        handleUpdateSubject,
        handleUpdateContent,
        attachments: emailDraftAttachments,
        handleUploadAttachments,
        handleDeleteAttachment: handleDeleteAttachmentById,
        handleDownloadAttachment: handleDownloadAttachmentById,
        handleSend: handleSendDraft,
        sendBlockedReason: attachmentSize?.exceedsSizeLimit ? OVER_ATTACHMENT_LIMIT_HINT : undefined,
        editorExtensions: DRAFT_EDITOR_EXTENSIONS,
      }) satisfies EmailDraftContextValue,
    [
      draft?.subject,
      placement,
      isMinimized,
      handleToggleMinimized,
      handleCloseFloating,
      canReturnInline,
      isEditable,
      isSending,
      recipientsOpen,
      handleUpdateRecipientsTo,
      handleUpdateRecipientsCc,
      handleUpdateRecipientsBcc,
      handleUpdateSubject,
      handleUpdateContent,
      emailDraftAttachments,
      handleUploadAttachments,
      handleDeleteAttachmentById,
      handleDownloadAttachmentById,
      handleSendDraft,
      attachmentSize?.exceedsSizeLimit,
    ]
  );

  useEffect(() => {
    if (draftLoadError) {
      const code = Number(draftLoadError.graphQLErrors?.[0]?.extensions?.code || 404);

      if (code === 410) {
        // GoneException
        toast({
          title: "Draft has already been sent",
          description: (
            <>
              You can find it in your{" "}
              <Button variant="link" size="sm" asChild>
                <Link to={`${rootPath}?labels=SENT`}>sent messages</Link>
              </Button>
              .
            </>
          ),
        });
      } else {
        toast({
          title: "Error loading draft",
          description: draftLoadError.message || "An unexpected error occurred",
          variant: "destructive",
        });
      }
      closeDraft(id);
    }
  }, [id, draftLoadError, closeDraft, toast]);

  return (
    <DraftContext.Provider value={value}>
      <EmailDraftProvider value={emailDraftValue}>
        <DraftEmailSignatureProvider value={signature}>{children}</DraftEmailSignatureProvider>
      </EmailDraftProvider>
    </DraftContext.Provider>
  );
};

export const useDraft = () => {
  const context = useContext(DraftContext);

  if (!context) {
    throw new Error("useDraft must be used within a DraftProvider");
  }

  return context;
};
